import { createColumnHelper } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import {
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { COLUMN_DEFAULT_SIZE_BASE } from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { ReportMetricsResponse } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { defaultColumnHelperAccessor } from 'src/tables/ReportTableCommon/DefaultColumnHelperAccessor';
import { LinkCell } from 'src/tables/ReportTableCommon/LinkCell';
import { SHIMMERING_DIV_HEIGHT_REPORT } from 'src/tables/ReportTableCommon/ReportTableCommon.constants';
import { reportV2AccessorFn } from 'src/tables/ReportTableCommon/ReportTableCommon.utils';
import { TableShimmeringDiv } from 'src/tables/Table';
import { ReportColumnPersonalizationFlags } from 'src/utils/columns/columnUtils.types';
import {
  CostOfGoodsSoldReportTableGroupingColumnId,
  CostOfGoodsSoldReportTableValueColumnId,
} from 'src/utils/columns/costOfGoodsSold/costOfGoodsSoldColumnUtils.types';
import {
  COST_OF_GOODS_SOLD_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID,
  COST_OF_GOODS_SOLD_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID,
} from 'src/utils/constants/contentIdMaps';
import { getPurchaseOrderRelativeUrl } from 'src/utils/purchaseUtils';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { ReportValueType } from 'src/WebApiController';

const columnHelper = createColumnHelper<ReportMetricsResponse | null>();

export const COST_OF_GOODS_SOLD_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG = {
  [CostOfGoodsSoldReportTableGroupingColumnId.AuditMonth]:
    columnHelper.accessor(
      reportV2AccessorFn(CostOfGoodsSoldReportTableGroupingColumnId.AuditMonth),
      {
        id: CostOfGoodsSoldReportTableGroupingColumnId.AuditMonth,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                COST_OF_GOODS_SOLD_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  CostOfGoodsSoldReportTableGroupingColumnId.AuditMonth
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_BASE,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ getValue, row: { original } }) {
          const { timeZone } = useSiteTimezoneContext();
          if (!original) {
            return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
          }

          const dateValue = getValue<string | null | undefined>();

          return (
            <TableCellDiv align="left">
              <div>
                {dateValue
                  ? formatInTimeZone(
                      new Date(dateValue),
                      timeZone ?? 'UTC',
                      'MMMM yyyy'
                    )
                  : ''}
              </div>
            </TableCellDiv>
          );
        },
      }
    ),
  [CostOfGoodsSoldReportTableGroupingColumnId.AuditWeek]: columnHelper.accessor(
    reportV2AccessorFn(CostOfGoodsSoldReportTableGroupingColumnId.AuditWeek),
    {
      id: CostOfGoodsSoldReportTableGroupingColumnId.AuditWeek,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              COST_OF_GOODS_SOLD_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                CostOfGoodsSoldReportTableGroupingColumnId.AuditWeek
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original } }) {
        const { timeZone } = useSiteTimezoneContext();
        if (!original) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }

        const dateValue = getValue<string | null | undefined>();

        const dateEndOfWeek = dateValue ? new Date(dateValue) : null;
        if (dateEndOfWeek) {
          dateEndOfWeek.setDate(dateEndOfWeek.getDate() + 6);
        }

        return (
          <TableCellDiv align="left">
            <div>
              {dateValue && dateEndOfWeek
                ? formatInTimeZone(
                    new Date(dateValue),
                    timeZone ?? 'UTC',
                    'yyyy/MM/dd'
                  ) +
                  ' - ' +
                  formatInTimeZone(
                    dateEndOfWeek,
                    timeZone ?? 'UTC',
                    'yyyy/MM/dd'
                  )
                : ''}
            </div>
          </TableCellDiv>
        );
      },
    }
  ),
  [CostOfGoodsSoldReportTableGroupingColumnId.AuditDay]: columnHelper.accessor(
    reportV2AccessorFn(CostOfGoodsSoldReportTableGroupingColumnId.AuditDay),
    {
      id: CostOfGoodsSoldReportTableGroupingColumnId.AuditDay,
      header: () => (
        <TableHeaderCellDiv align={'left'}>
          <Content
            id={
              COST_OF_GOODS_SOLD_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                CostOfGoodsSoldReportTableGroupingColumnId.AuditDay
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original } }) {
        const { timeZone } = useSiteTimezoneContext();
        if (!original) {
          return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
        }

        const dateValue = getValue<string | null | undefined>();

        return (
          <TableCellDiv align="left">
            <div>
              {dateValue
                ? formatInTimeZone(
                    new Date(dateValue),
                    timeZone ?? 'UTC',
                    'yyyy/MM/dd'
                  )
                : ''}
            </div>
          </TableCellDiv>
        );
      },
    }
  ),
  [CostOfGoodsSoldReportTableGroupingColumnId.PurchaseOrderId]:
    columnHelper.accessor(
      reportV2AccessorFn(
        CostOfGoodsSoldReportTableGroupingColumnId.PurchaseOrderId
      ),
      {
        id: CostOfGoodsSoldReportTableGroupingColumnId.PurchaseOrderId,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                COST_OF_GOODS_SOLD_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  CostOfGoodsSoldReportTableGroupingColumnId.PurchaseOrderId
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: ({ getValue }) => {
          const purchaseOrderId = getValue<string | null>();

          if (!purchaseOrderId) {
            return <></>;
          }

          return (
            <LinkCell
              text={`#${purchaseOrderId}`}
              url={getPurchaseOrderRelativeUrl(purchaseOrderId)}
              separator={<span>{', '}</span>}
              openInNewTab
              align="left"
              style={{ fontWeight: 600 }}
            />
          );
        },
      }
    ),

  [CostOfGoodsSoldReportTableGroupingColumnId.TicketGroupId]:
    columnHelper.accessor(
      reportV2AccessorFn(
        CostOfGoodsSoldReportTableGroupingColumnId.TicketGroupId
      ),
      {
        id: CostOfGoodsSoldReportTableGroupingColumnId.TicketGroupId,
        header: () => (
          <TableHeaderCellDiv align={'left'}>
            <Content
              id={
                COST_OF_GOODS_SOLD_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
                  CostOfGoodsSoldReportTableGroupingColumnId.TicketGroupId
                ]
              }
            />
          </TableHeaderCellDiv>
        ),
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: ({ getValue }) => {
          const ticketGroupId = getValue<string | null>();

          if (!ticketGroupId) {
            return <></>;
          }

          return (
            <TableCellDiv align="left">
              <span style={{ fontWeight: 600 }}>#{ticketGroupId}</span>
            </TableCellDiv>
          );
        },
      }
    ),
} as const;

export const costOfGoodsSoldReportTableColumnDef = (
  columnId: string,
  columnValue: string,
  valueType: ReportValueType | undefined,
  personalizationFlags: Partial<ReportColumnPersonalizationFlags> | undefined
) => {
  const specialConfig = Object.entries(
    COST_OF_GOODS_SOLD_REPORT_SPECIAL_TABLE_COLUMNS_CONFIG
  ).find(([key]) => key === columnId)?.[1];

  if (specialConfig) {
    return specialConfig;
  }

  const headerContentId =
    COST_OF_GOODS_SOLD_REPORT_TABLE_GROUPING_COLUMN_ID_TO_CONTENT_ID[
      columnId as CostOfGoodsSoldReportTableGroupingColumnId
    ] ||
    COST_OF_GOODS_SOLD_REPORT_TABLE_VALUE_COLUMN_ID_TO_CONTENT_ID[
      columnId as CostOfGoodsSoldReportTableValueColumnId
    ];

  return defaultColumnHelperAccessor(
    columnHelper,
    columnId,
    columnValue,
    headerContentId,
    ReportTypesV2.CostOfGoodsSold,
    valueType,
    personalizationFlags
  );
};
